"use strict";
// Consent-gated GA4 runtime (WP-07), testable without Gatsby. Basic consent mode: before a decision
// and after refusal there is no Google script, cookie, dataLayer or ping. The Gatsby wrapper
// (analytics.js) passes only the build-time public ID: no test override exists in shipped code and
// loopback hosts are always blocked. Browser-safe syntax (no lookbehind, no Node APIs at runtime).
const catalog = require("./analytics-events.cjs");
const CONSENT_KEY = "sr-analytics-consent", SCRIPT_ID = "sr-ga", GTAG_ORIGIN = "https://www.googletagmanager.com";
const ID_PATTERN = /^G-[A-Z0-9]+$/, PUBLISH_ID = /^G-[A-Z0-9]{6,16}$/;
const PLACEHOLDER_ID = /^G-(?:TEST|X{3,}|0{4,}|1234|DEMO|DUMMY|EXAMPLE|FAKE|SAMPLE|PLACEHOLDER|LOCAL|STUB|HARNESS)/;
const LOOPBACK = /^(?:(?:.+\.)?localhost|127(?:\.\d{1,3}){3}|0\.0\.0\.0|\[::1\]|::1)$/i;
const DENIED = { analytics_storage: "denied", ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied" };
const isLoopbackHost = hostname => LOOPBACK.test(String(hostname || ""));

// Release guard for build scripts. OFF (no ID) is a valid, honest profile; declared ON without a
// real public ID fails. A declared OFF with an ID is contradictory and fails too.
// Gatsby also reads GATSBY_* from .env.production (process.env wins): pass its text as dotenvText.
function parseDotenv(text) {
  const out = {};
  for (const line of String(text || "").split(/\r?\n/)) {
    const match = /^\s*(?:export\s+)?([A-Za-z_][A-Za-z0-9_]*)\s*=\s*(.*?)\s*$/.exec(line);
    if (!match || line.trim().startsWith("#")) continue;
    const value = match[2], quoted = /^(['"])(.*)\1$/.exec(value);
    out[match[1]] = quoted ? quoted[2] : value.replace(/\s+#.*$/, "");
  }
  return out;
}
function assertPublishableAnalyticsProfile(env = process.env, { dotenvText, defaults = {} } = {}) {
  const file = parseDotenv(dotenvText), pick = key => env[key] !== undefined && env[key] !== "" ? env[key] : file[key];
  const declared = String(pick("SR_ANALYTICS_PROFILE") ?? defaults.profile ?? "").trim().toLowerCase();
  const id = String(pick("GATSBY_GA_MEASUREMENT_ID") ?? (declared === "off" ? "" : defaults.measurementId) ?? "").trim();
  if (declared && declared !== "on" && declared !== "off") throw new Error('SR_ANALYTICS_PROFILE musi mieć wartość "on" albo "off".');
  if (declared === "off") {
    if (id) throw new Error("Profil analityki OFF, ale ustawiono GATSBY_GA_MEASUREMENT_ID. Usuń zmienną albo zadeklaruj SR_ANALYTICS_PROFILE=on.");
    return { profile: "off", declared: true, measurementId: null };
  }
  if (!id) {
    if (declared === "on") throw new Error("Profil analityki ON wymaga publicznego GATSBY_GA_MEASUREMENT_ID (G-…) z właściwości GA4 w środowisku buildu. Bez niego wybierz SR_ANALYTICS_PROFILE=off.");
    return { profile: "off", declared: false, measurementId: null };
  }
  if (!PUBLISH_ID.test(id)) throw new Error("GATSBY_GA_MEASUREMENT_ID ma niepoprawny format (oczekiwano G- i 6–16 wielkich liter/cyfr).");
  if (PLACEHOLDER_ID.test(id)) throw new Error("GATSBY_GA_MEASUREMENT_ID wygląda na atrapę testową. Publikowany build wymaga prawdziwego ID właściwości albo profilu OFF.");
  return { profile: "on", declared: declared === "on", measurementId: id };
}
function defaultStorage() {
  const memory = new Map();
  return {
    get(key) { try { const value = globalThis.localStorage.getItem(key); return value ?? memory.get(key) ?? null; } catch { return memory.get(key) ?? null; } },
    set(key, value) { try { globalThis.localStorage.setItem(key, value); memory.delete(key); } catch { memory.set(key, value); } },
  };
}
// Host-only plus every parent domain above the public suffix guess (.spec-ranking.pl for www.*).
function cookieDomains(hostname) {
  const host = String(hostname || "").toLowerCase().replace(/^\[|\]$/g, "");
  if (!host || /^[\d.]+$/.test(host) || host.includes(":") || !host.includes(".")) return [null];
  const labels = host.split("."), domains = [null];
  for (let i = 0; i < labels.length - 1; i++) domains.push("." + labels.slice(i).join("."));
  return domains;
}
function destinationFor(link) {
  const host = String(link.hostname || "").toLowerCase(), path = String(link.pathname || ""), search = String(link.search || ""), hash = String(link.hash || "");
  if (/(?:^|\.)ceneo\.pl$/.test(host)) return /^\/;szukaj-/.test(path) ? "search_results" : /tab=reviews/.test(hash) ? "reviews" : "product_page";
  if (host === "wvw.spec-ranking.pl") {
    if (/^\/allegroSearch/i.test(path)) return "search_results";
    if (/^\/letyGo/i.test(path)) return /[?&]q=2(?:&|$)/.test(search) ? "browser_extension" : "cashback_portal";
    if (/^\/prmDrct/i.test(path)) return "promo_landing";
  }
  return "other";
}
function navTarget(link) {
  const path = String(link.pathname || "").replace(/\/+$/, "");
  if (path === "/fake-friday") return "fake_friday";
  if (path.startsWith("/historia-ceny")) return "price_history";
  if (path === "/ceny-paliw") return "fuel";
  return path === "" && /rankingi/.test(String(link.hash || "")) ? "rankings" : "other";
}

function createAnalytics(options = {}) {
  const id = typeof options.measurementId === "string" ? options.measurementId.trim() : "";
  const configured = ID_PATTERN.test(id), container = configured ? id.slice(2) : null;
  const storage = options.storage || defaultStorage(), now = options.now || (() => Date.now());
  const win = () => options.window || (typeof window !== "undefined" ? window : undefined);
  const doc = () => options.document || (typeof document !== "undefined" ? document : undefined);
  const warn = options.warn || ((...args) => { if (options.debug && typeof console !== "undefined") console.warn(...args); });
  const state = { view: 0, path: null, location: undefined, referrer: undefined, sentView: -1, queue: [], once: new Set(), timers: new Map(), context: null, dom: null };

  const consentState = () => { const value = storage.get(CONSENT_KEY); return value === "accepted" || value === "declined" ? value : "pending"; };
  function subscribeConsent(listener) {
    const w = win(), read = () => listener(consentState());
    const changed = event => { if (event.key === CONSENT_KEY || event.key === null) read(); };
    read();
    if (!w || typeof w.addEventListener !== "function") return () => {};
    w.addEventListener("sr-consent", read); w.addEventListener("storage", changed);
    return () => { w.removeEventListener("sr-consent", read); w.removeEventListener("storage", changed); };
  }
  const isLocalEnvironment = () => { const w = win(); return !w || !w.location || isLoopbackHost(w.location.hostname); };
  function gate() {
    if (!win() || !doc()) return "ssr";
    if (!configured) return "profile_off";
    if (isLocalEnvironment()) return "loopback";
    const consent = consentState();
    return consent === "accepted" ? null : consent === "declined" ? "consent_declined" : "consent_pending";
  }
  function analyticsProfile() {
    const reason = gate(), local = isLocalEnvironment();
    return { schemaVersion: catalog.SCHEMA_VERSION, profile: configured ? "on" : "off", reason: configured ? null : id ? "measurement_id_invalid" : "measurement_id_missing", measurementIdPresent: configured, loopback: local, consent: consentState(), bannerRequired: configured && !local, trackingActive: reason === null, track: reason === null ? "active" : configured ? "blocked:" + reason : "noop" };
  }
  function gtag() {
    const w = win(); w.dataLayer = w.dataLayer || [];
    if (typeof w.gtag !== "function") w.gtag = function gtag() { w.dataLayer.push(arguments); };
    w.gtag.apply(w, arguments);
  }
  const currentLocation = () => catalog.redactLocation(win().location.href);
  function pageFields() {
    const fields = { page_location: state.location || currentLocation(), page_referrer: state.referrer, page_title: catalog.redactTitle(doc().title) };
    for (const key of Object.keys(fields)) if (!fields[key]) delete fields[key];
    return fields;
  }
  function ensureTag() {
    const w = win(), d = doc(); w["ga-disable-" + id] = false;
    if (d.getElementById(SCRIPT_ID)) return false;
    gtag("consent", "default", { analytics_storage: "granted", ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied" });
    gtag("js", new Date(now()));
    gtag("config", id, { send_page_view: false, allow_google_signals: false, allow_ad_personalization_signals: false, ...pageFields() });
    const script = d.createElement("script"); script.id = SCRIPT_ID; script.async = true; script.src = `${GTAG_ORIGIN}/gtag/js?id=${encodeURIComponent(id)}`;
    d.head.appendChild(script);
    // Withdrawal in another tab must also silence a tag already loaded in this one.
    if (typeof w.addEventListener === "function") w.addEventListener("storage", event => { if ((event.key === CONSENT_KEY || event.key === null) && event.newValue !== "accepted") revoke(); });
    return true;
  }
  function sendPageView() {
    if (state.sentView === state.view || gate() !== null) return false;
    const fields = pageFields(), result = catalog.validateEvent("page_view", { ...fields, page_type: pageType() });
    gtag("set", fields); gtag("event", "page_view", result.params);
    state.sentView = state.view;
    const queued = state.queue; state.queue = [];
    for (const [name, params] of queued) gtag("event", name, params);
    return true;
  }
  function defer(fn) {
    if (options.defer) return options.defer(fn);
    const w = win(); let done = false; const run = () => { if (!done) { done = true; fn(); } };
    w.setTimeout(run, 100); // background tabs pause rAF; the timeout still settles the title
    if (typeof w.requestAnimationFrame === "function") w.requestAnimationFrame(() => w.requestAnimationFrame(() => w.setTimeout(run, 0)));
  }
  function clearTimers() { const w = win(); for (const timer of state.timers.values()) (options.clearTimeout || (w && w.clearTimeout.bind(w)) || clearTimeout)(timer); state.timers.clear(); }
  function pageType() { const w = win(), path = w && w.location.pathname; return state.context && state.context.path === path && state.context.values.page_type || catalog.pageTypeFor(path); }

  // Gatsby onRouteUpdate contract: one page_view per real navigation (pathname change). The initial
  // call (prevLocation=null) is the only first-load page_view; a repeated initial call (hydration,
  // StrictMode) and query/hash-only updates (?q=) are ignored. GA config uses send_page_view:false.
  function handleRouteUpdate(location, prevLocation) {
    const w = win(); if (!w || !doc()) return { pageView: false, reason: "ssr" };
    const current = location && location.pathname ? location : w.location, path = current.pathname;
    const href = typeof current.href === "string" && current.href ? current.href : w.location.origin + path + (current.search || "");
    if (prevLocation && prevLocation.pathname === path) return { pageView: false, reason: "same_path", view: state.view };
    if (!prevLocation && state.view > 0 && state.path === path) return { pageView: false, reason: "duplicate_initial", view: state.view };
    state.referrer = state.view === 0 ? referrerFor(doc().referrer, w) : state.location;
    if (state.view > 0) { state.queue = []; clearTimers(); }
    state.view += 1; state.path = path; state.location = catalog.redactLocation(href); state.once.clear(); state.dom = null;
    const reason = gate(); if (reason) return { pageView: false, reason, view: state.view };
    ensureTag();
    const view = state.view; defer(() => { if (state.view === view) sendPageView(); });
    return { pageView: true, view };
  }
  function referrerFor(referrer, w) {
    if (!referrer) return undefined;
    try { return new URL(referrer).origin === w.location.origin ? catalog.redactLocation(referrer, { max: catalog.LIMITS.page_referrer }) : catalog.redactLocation(referrer, { externalOriginOnly: true, max: catalog.LIMITS.page_referrer }); } catch { return undefined; }
  }

  function track(name, params) {
    if (name === "page_view") return { sent: false, reason: "reserved" };
    const w = win(), result = catalog.validateEvent(name, params, { pageType: w ? pageType() : undefined, now: now(), reserve: 1 });
    if (result.errors.length) warn(`[analytics] ${name}: ${result.errors.join(", ")}`);
    if (!result.ok) return { sent: false, reason: "invalid", errors: result.errors };
    const reason = gate(); if (reason) return { sent: false, reason, params: result.params, errors: result.errors };
    const payload = { ...result.params, page_location: currentLocation() };
    if (state.sentView !== state.view) { if (state.queue.length >= 25) return { sent: false, reason: "queue_full" }; state.queue.push([name, payload]); return { sent: true, queued: true, params: payload, errors: result.errors }; }
    gtag("event", name, payload);
    return { sent: true, params: payload, errors: result.errors };
  }
  function trackOnce(key, name, params) {
    const w = win(), scoped = state.view + "|" + (w ? w.location.pathname : "") + "|" + key;
    if (state.once.has(scoped)) return { sent: false, reason: "duplicate" };
    const result = track(name, params); if (result.sent) state.once.add(scoped);
    return result;
  }
  // Debounced "settled" variant (calculator results): the last call within `delay` ms wins and is
  // sent at most once per key and view. Navigation or cancelSettled(key) drops a pending call.
  function trackSettled(key, name, params, delay = 1500) {
    const w = win(); if (!w) return { scheduled: false, reason: "ssr" };
    cancelSettled(key);
    const view = state.view, schedule = options.setTimeout || w.setTimeout.bind(w);
    state.timers.set(key, schedule(() => { state.timers.delete(key); if (state.view === view) trackOnce(key, name, typeof params === "function" ? params() : params); }, delay));
    return { scheduled: true };
  }
  function cancelSettled(key) { const w = win(); if (!state.timers.has(key)) return false; (options.clearTimeout || w.clearTimeout.bind(w))(state.timers.get(key)); state.timers.delete(key); return true; }
  // Optional explicit context for the current pathname (ProductPage); DOM JSON is the fallback.
  function setPageContext(values) { const w = win(); if (w && values && typeof values === "object") state.context = { path: w.location.pathname, values: { ...values } }; }
  function productContext() {
    const w = win(), d = doc(), key = state.view + "|" + w.location.pathname;
    if (!state.dom || state.dom.key !== key) {
      const values = {};
      try { const p = JSON.parse((d.getElementById("sr-product-data") || {}).textContent || "null"); if (p && typeof p === "object") { if (/^\d{1,12}$/.test(String(p.i))) values.product_id = String(p.i); if (p.lpd) values.last_price_date = String(p.lpd); } } catch { /* no context */ }
      try { const series = JSON.parse((d.getElementById("sr-price-history") || {}).textContent || "null"); if (Array.isArray(series)) values.history_points = series.length; } catch { /* no context */ }
      state.dom = { key, values };
    }
    const explicit = state.context && state.context.path === w.location.pathname ? state.context.values : {};
    return { ...state.dom.values, ...explicit };
  }
  function linkEvent(link) {
    const data = link.dataset || {};
    if (data.merchant !== undefined && data.placement !== undefined) {
      const context = productContext(), host = String(link.hostname || "").toLowerCase();
      return ["merchant_outbound_click", { merchant: data.merchant, placement: data.placement, destination: data.destination || destinationFor(link), link_type: "affiliate", link_domain: catalog.LINK_DOMAINS.includes(host) ? host : undefined, product_id: context.product_id, has_history: context.has_history, history_points: context.history_points, last_price_date: context.last_price_date, data_age_days: context.last_price_date ? undefined : context.data_age_days }, true];
    }
    if (data.navCta !== undefined) return ["nav_cta_click", { placement: data.navCta, target: data.navTarget || navTarget(link) }, false];
    if (data.resultPosition !== undefined) return ["search_result_click", { search_source: data.searchSource, result_position: data.resultPosition, result_count: data.resultCount, link_area: data.linkArea, product_id: data.productId }, false];
    return null;
  }
  // Delegated measurement: click (button 0; keyboard Enter dispatches a native click) and auxclick
  // (button 1, middle). Right button (2), clicks outside an anchor and a prevented external
  // navigation are ignored. One DOM event can be measured only once.
  function handleLinkEvent(event) {
    if (!event || event.__srAnalytics) return null;
    if (!(event.type === "click" && event.button === 0) && !(event.type === "auxclick" && event.button === 1)) return null;
    const target = event.target, element = target && target.nodeType === 1 ? target : target && target.parentElement;
    const link = element && typeof element.closest === "function" ? element.closest("a[href]") : null;
    if (!link) return null;
    const found = linkEvent(link); if (!found) return null;
    if (found[2] && event.defaultPrevented) return null; // external CTA did not navigate
    event.__srAnalytics = true;
    return track(found[0], found[1]);
  }
  function installLinkTracking(target) {
    const d = target || doc(); if (!d || d.__srAnalyticsLinks) return () => {};
    d.addEventListener("click", handleLinkEvent); d.addEventListener("auxclick", handleLinkEvent); d.__srAnalyticsLinks = true;
    return () => { d.removeEventListener("click", handleLinkEvent); d.removeEventListener("auxclick", handleLinkEvent); d.__srAnalyticsLinks = false; };
  }

  function deleteGoogleCookies() {
    const w = win(), d = doc(); if (!w || !d || !configured) return [];
    const names = ["_ga", "_ga_" + container], secure = w.location.protocol === "https:" ? "; Secure" : "";
    const present = names.filter(name => String(d.cookie || "").split(/;\s*/).some(pair => pair.split("=")[0] === name));
    for (const name of names) for (const domain of cookieDomains(w.location.hostname)) d.cookie = `${name}=; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/${domain ? "; Domain=" + domain : ""}${secure}; SameSite=Lax`;
    return present;
  }
  function revoke() {
    const w = win(); state.queue = []; clearTimers();
    if (!configured || !w || !doc()) return [];
    w["ga-disable-" + id] = true;
    if (typeof w.gtag === "function" && doc().getElementById(SCRIPT_ID)) gtag("consent", "update", { ...DENIED });
    return deleteGoogleCookies();
  }
  function setConsent(accepted) {
    const w = win();
    storage.set(CONSENT_KEY, accepted ? "accepted" : "declined");
    if (accepted) { if (gate() === null) { if (!ensureTag()) gtag("consent", "update", { analytics_storage: "granted" }); if (state.view > 0) sendPageView(); } }
    else revoke();
    const EventClass = w && (w.Event || (typeof Event !== "undefined" ? Event : null));
    if (w && EventClass && typeof w.dispatchEvent === "function") w.dispatchEvent(new EventClass("sr-consent"));
    return analyticsProfile();
  }
  return { configured, consentKey: CONSENT_KEY, isLocalEnvironment, consentState, subscribeConsent, analyticsProfile, setConsent, track, trackOnce, trackSettled, cancelSettled, setPageContext, handleRouteUpdate, handleLinkEvent, installLinkTracking, deleteGoogleCookies,
    debugState: () => ({ view: state.view, sentView: state.sentView, queued: state.queue.length, pendingSettled: [...state.timers.keys()] }) };
}
module.exports = { createAnalytics, assertPublishableAnalyticsProfile, parseDotenv, isLoopbackHost, cookieDomains, destinationFor, CONSENT_KEY, SCRIPT_ID, GTAG_ORIGIN, ID_PATTERN, catalog };
